export interface ClientMembershipsPagination {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
}

const pickNumber = (...values: unknown[]): number | undefined => {
    for (const value of values) {
        if (value === null || value === undefined || value === '') continue;
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }

    return undefined;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
};

export const unwrapPurchaseHistoryPayload = (raw: unknown): Record<string, unknown> => {
    let value: unknown = raw;

    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return {};
        }
    }

    if (Array.isArray(value)) {
        return { enrollments: value };
    }

    const root = asRecord(value);
    if (!root) return {};

    const nested = asRecord(root.data);
    if (nested && (nested.enrollments || nested.total_count != null || nested.totalCount != null)) {
        return { ...root, ...nested };
    }

    return root;
};

const readFromSources = (sources: Array<Record<string, unknown> | null>, keys: string[]): number | undefined => {
    for (const source of sources) {
        if (!source) continue;
        const parsed = pickNumber(...keys.map(key => source[key]));
        if (parsed !== undefined) return parsed;
    }

    return undefined;
};

export const parsePurchaseHistoryPagination = (
    raw: unknown,
    fallbackPage: number,
    fallbackSize: number,
    pageLength: number
): ClientMembershipsPagination => {
    const data = unwrapPurchaseHistoryPayload(raw);
    const sources = [data, asRecord(data.meta), asRecord(data.pagination), asRecord(data.page)];
    const pageSize = readFromSources(sources, ['per_page', 'perPage', 'size']) ?? fallbackSize;
    const currentPage = readFromSources(sources, ['current_page', 'currentPage', 'page']) ?? fallbackPage;
    const nextPage = readFromSources(sources, ['next_page', 'nextPage']);
    const totalFromApi = readFromSources(sources, [
        'total_count',
        'totalCount',
        'total_enrollments',
        'totalEnrollments',
        'total'
    ]);
    const totalPagesFromApi = readFromSources(sources, ['total_pages', 'totalPages']);
    const hasNextPage = nextPage != null && nextPage > currentPage;
    const totalPages = Math.max(
        1,
        totalPagesFromApi ?? (hasNextPage ? Math.max(currentPage + 1, nextPage ?? 1) : 1),
        pageSize === 0 ? 1 : Math.ceil((totalFromApi ?? 0) / pageSize) || 1
    );
    const totalCount =
        totalFromApi ?? (totalPages > 1 ? Math.max(pageLength, (totalPages - 1) * pageSize + 1) : pageLength);

    return {
        currentPage,
        totalPages,
        totalCount,
        hasNextPage: hasNextPage || currentPage < totalPages
    };
};
